import { moduleApi } from "@amtana/module";
import type { EraseStep } from "@amtana/module";

import type { ModelCountRequest, ModelRelayAnswer, ModelRequest } from "./agent-model.schemas.ts";
import type {
  Agent,
  AgentIdInput,
  AgentLastTask,
  AgentOffers,
  AgentSelf,
  AgentStatus,
  CreateAgentInput,
  ListAgentsOutput,
  Ok,
  RenameAgentInput,
  TurnTokenVerdict,
} from "./agent.schemas.ts";
import type { AgentToolList } from "./agent.tools.ts";

/** One call to our model through the model door (D670): who called, the turn it is part of (the
 * relay's `X-Zaistev-Turn`), the betas the harness speaks, and the request itself. */
export type ModelCall<TRequest> = AgentCaller & {
  readonly turnToken: string | undefined;
  readonly beta: string | undefined;
  readonly request: TRequest;
};

/** Who asks, and where: a member acts on their own agents in their active organization. */
export type ForMember = { readonly organizationId: string; readonly userId: string };

/** One agent named by its id inside an organization, for a peer that already checked who asks. */
export type AgentRef = { readonly agentId: string; readonly organizationId: string };

/** The agent itself, as its key resolved it at its own door (ADR-023 §1): the owner it acts for
 * and what it may do there now, the owner's live role cut to its own list. */
export type AsAgent = AgentRef & { readonly ownerUserId: string; readonly permissions: readonly string[] };

/** Who called an agent's own door, as the door hands it over: the owner as `userId`, the agent,
 * the organization and what the agent may do there now. */
export type AgentCaller = {
  readonly userId: string;
  readonly agentId?: string;
  readonly organizationId?: string;
  readonly permissions?: readonly string[];
};

// The promise modules/agent makes. The first block is what a member's doors call; the second is
// peer-only, for the lanes that build on agents (the runtime, the relay, the vault): the agent's
// key, the per-turn token, the agent token the host presents to the supervisor, and the pool
// host's bearer. Resolving a key to a session is not here: that is `@amtana/process`'s
// `AgentKeyProvider`, which this module's installer offers.
// Peer-only `eraseStep` (ADR-026, epic 30, S30.14, D924): every agent the erased person owns, in
// every organization, deleted outright; its key stops resolving with it.
export interface AgentApi extends EraseStep {
  /** A record only: nothing wakes, nothing starts (ADR-023 §1). Refused as `model_key_missing`
   * for our model while no model key is set (D550). */
  create(input: CreateAgentInput & ForMember): Promise<Agent>;
  /** The caller's own agents in their organization, newest first. */
  list(input: ForMember): Promise<ListAgentsOutput>;
  get(input: AgentIdInput & ForMember): Promise<Agent>;
  rename(input: RenameAgentInput & ForMember): Promise<Agent>;
  /** Deletes the record; its key stops resolving on the next request. */
  delete(input: AgentIdInput & ForMember): Promise<Ok>;
  /** The harnesses a new agent may run on right now. */
  offers(input: ForMember): Promise<AgentOffers>;

  /** The agent's own door: who it is. */
  me(input: AgentCaller): Promise<AgentSelf>;
  /** The agent's own door: every tool (agent.tools.ts), each marked whether it may run it. */
  tools(input: AgentCaller): Promise<AgentToolList>;
  /** The agent's own door: runs one tool by name with its input, checked against the tool's
   * schema and permission; answers the tool's own output. */
  runTool(input: AgentCaller & { readonly name: string; readonly input: unknown }): Promise<unknown>;
  /** The agent's own door: one call to our model (ADR-023 §7, D670), with our key added here and
   * its tokens drawn from the organization's AI credits as it ends (D675). Refused as
   * `model_key_missing`, `model_turn_invalid`, `model_not_this_agent` or `ai_credits_used`. */
  modelMessages(input: ModelCall<ModelRequest>): Promise<ModelRelayAnswer>;
  /** The agent's own door: counts a request's tokens on our model, drawing nothing. */
  modelCountTokens(input: ModelCall<ModelCountRequest>): Promise<ModelRelayAnswer>;

  /** Peer-only. Each agent's name by its id, for the audit log; an id it does not know is left out. */
  namesOf(input: {
    readonly organizationId: string;
    readonly agentIds: readonly string[];
  }): Promise<Readonly<Record<string, string>>>;
  /** Peer-only. Any agent of the organization, or null. */
  find(input: AgentRef): Promise<Agent | null>;
  /** Peer-only. Mints a fresh key `zk_<id>.<secret>` for the host relay, replacing the last one. */
  issueKey(input: AgentRef): Promise<{ readonly key: string }>;
  /** Peer-only. What the runtime reports: the live status, and the last task when one started. */
  setStatus(
    input: AgentRef & { readonly status: AgentStatus; readonly lastTask?: AgentLastTask },
  ): Promise<void>;
  /** Peer-only. Seam 1: a turn token for one turn of this agent, valid `ttlSeconds` (two hours). */
  mintTurnToken(input: AgentRef & { readonly turnId: string; readonly ttlSeconds?: number }): Promise<string>;
  /** Peer-only. Seam 1, checked in constant time against `AGENT_TURN_SECRET`. */
  verifyTurnToken(input: { readonly token: string }): Promise<TurnTokenVerdict>;
  /** Peer-only. Seam 2: `zat_<agentId>.<hmac>` under the computer's own key, for the host's
   * hello to the supervisor on that computer (S27.14, D740). */
  agentToken(input: { readonly agentId: string; readonly computerId: string }): Promise<string>;
  /** Peer-only. Seam 2, in constant time; null when it names no agent this computer's key signed. */
  verifyAgentToken(input: {
    readonly token: string;
    readonly computerId: string;
  }): Promise<{ readonly agentId: string } | null>;
  /** Peer-only. Seam 3: whether a bearer is the pool host's own (`POOL_HOST_TOKEN`), in constant time. */
  isPoolHost(input: { readonly bearer: string }): Promise<boolean>;
}

export const AgentApi = moduleApi<AgentApi>()("agent");
